import { useMainStore } from '@/stores/main'

import axios, { type AxiosError, type AxiosInstance } from 'axios'
import axiosRetry from 'axios-retry'

import { recordApiSuccess, recordRetryableFailure, retriesAllowed } from '@/lib/apiRetrySuppression'
import { reportCartBusyRefusal } from '@/lib/cartBusyTelemetry'
import { installDevApiFaults } from '@/lib/devApiFaults'
import { DEV_TOOLS_ENABLED } from '@/lib/devToolsAccess'
import { appErrorService, claimAxiosErrorReport } from '@/services/AppErrorService'

// RFC 9110 idempotent methods. `POST` is excluded deliberately: the only POST
// that is safe to replay is the catalog read, and singling it out would leave a
// new mutation retryable by default the day someone forgets to exclude it.
const IDEMPOTENT_METHODS = new Set(['get', 'head', 'options', 'put', 'delete'])

function isIdempotentMethod(method: string | undefined): boolean {
  return method !== undefined && IDEMPOTENT_METHODS.has(method.toLowerCase())
}

// Statuses where a retry has a real chance of landing on a healthy path: a bad
// gateway, a node out of rotation, an upstream timeout. A bare 500 is excluded —
// during an overload it is the server saying it is struggling, and the answer to
// that is to stop asking, not to ask again.
const RETRYABLE_STATUSES = new Set([502, 503, 504])

// axios-retry's default `delayFactor` of 100ms puts the two retries at ~200ms and
// ~400ms, which is a triple-tap rather than a backoff. One second spaces them at
// ~2s and ~4s (`retryCount` starts at 1, so 2¹ and 2² times the factor).
// `exponentialDelay` already takes `Math.max(calculated, Retry-After)`, so a server
// that asks for longer still gets it.
const RETRY_DELAY_FACTOR_MS = 1_000

class Sunny16BaseService {
  protected axiosInstance: AxiosInstance

  /**
   * `timeoutMs` is opt-in per service rather than a shared default. The content API returns a
   * ~3 MB catalog payload that legitimately takes a long time on a slow mobile connection, and
   * aborting that would be a worse regression than the timeout prevents. Bound the services whose
   * responses are small and whose silence is dangerous — see `Sunny16CommerceCartService`.
   */
  constructor(baseUrl: string, timeoutMs?: number) {
    this.axiosInstance = axios.create({
      baseURL: baseUrl,
      timeout: timeoutMs,
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache'
      }
    })

    if (DEV_TOOLS_ENABLED) installDevApiFaults(this.axiosInstance)

    axiosRetry(this.axiosInstance, {
      retries: 2,
      retryDelay: (retryCount, error) =>
        axiosRetry.exponentialDelay(retryCount, error, RETRY_DELAY_FACTOR_MS),
      retryCondition: (error) => {
        // Replaying a request is only safe when the request itself is. Every POST
        // this app makes is a mutation except `/catalog/`, and a timed-out POST
        // reports as a network error — so the previous condition replayed cart
        // upserts and order creation twice. Rationale:
        // `docs/features/2026.09.18-cart-state-reconciliation/implementation-plans/2026.09.18-001-cart-state-reconciliation.md`
        //
        // A network error is the case worth retrying: the request never reached the
        // server, so a retry costs it nothing. A 5xx did reach it, so retrying is
        // load it is already failing to carry — allowed only for the statuses that
        // suggest a different path might succeed. Client timeouts never reach here;
        // axios-retry's `isNetworkError` excludes `ECONNABORTED` by design.
        const status = error.response?.status
        const retryableShape =
          axiosRetry.isNetworkError(error) ||
          (status !== undefined && RETRYABLE_STATUSES.has(status))

        // Counted before the method check: a failed `POST` says as much about the API's
        // health as a failed `GET`, even though it will never be retried itself.
        if (retryableShape) recordRetryableFailure()
        if (!retryableShape) return false

        if (!isIdempotentMethod(error.config?.method)) return false

        // Per-request backoff bounds one caller; this bounds all of them together.
        return retriesAllowed()
      },
      onRetry: (retryCount, error, requestConfig) => {
        console.warn(
          `[Sunny16BaseService] Retry #${retryCount} for ${requestConfig.method?.toUpperCase()} ${requestConfig.url}`,
          error.message
        )
      }
    })

    this.axiosInstance.interceptors.response.use(
      (response) => {
        // The API is answering, so any failure streak the retry budget was counting is over.
        recordApiSuccess()
        return response
      },
      (err) => {
        // Counted here, ahead of the opt-out below, because every caller marks it expected.
        reportCartBusyRefusal(err)

        // Per-call opt-out: a request config may carry `_expectedError`, a
        // predicate returning true for response shapes the caller has chosen
        // to treat as benign (e.g. duplicate-tap DELETE → 400 "Cart Bundle
        // not found"). When matched, skip the New Relic report and let the
        // rejection propagate so the caller's own catch still runs.
        const expectedPredicate = (err.config as any)?._expectedError as
          | ((error: AxiosError) => boolean)
          | undefined
        if (typeof expectedPredicate === 'function' && expectedPredicate(err)) {
          // Claimed unreported, so a caller that lets it escape doesn't get it reported elsewhere.
          claimAxiosErrorReport(err)
          return Promise.reject(err)
        }
        // Each `axios-retry` attempt re-enters this chain, so an exhausted request's final error
        // arrives once per attempt as the rejection unwinds; only the first claim reports it.
        if (!claimAxiosErrorReport(err)) {
          return Promise.reject(err)
        }
        const mainStore = useMainStore()
        appErrorService.reportAxiosError(err, {
          cadyMarket: mainStore.cadyMarket,
          cadySessionToken: mainStore.cadySessionToken
        })
        return Promise.reject(err)
      }
    )
  }
}

export default Sunny16BaseService
